import { useEffect, useState, useId, type ChangeEvent, type DragEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TemplateVisibility } from '@pius/contracts';
import { Notice } from '../../components/primitives';
import { createTemplate, startDraft, saveLocalization, type ApiError } from './api';
import { validateTemplateBundle, type TemplateBundle } from './templateBundle';
import './template-modals.css';

interface ImportTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ImportTemplateModal({
  isOpen,
  onClose,
}: ImportTemplateModalProps): React.JSX.Element | null {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputId = useId();

  const [rawText, setRawText] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [visibility, setVisibility] = useState<TemplateVisibility>('private');
  const [parsedBundle, setParsedBundle] = useState<TemplateBundle | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleClose = () => {
    setRawText('');
    setTemplateName('');
    setVisibility('private');
    setParsedBundle(null);
    setValidationErrors([]);
    setIsDragging(false);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!parsedBundle) throw new Error('No valid template bundle parsed.');

      // 1. Create template record
      const created = await createTemplate({
        name: templateName.trim() || parsedBundle.template.name,
        visibility,
      });

      // 2. Start draft with imported definition
      const draft = await startDraft(created.id, parsedBundle.template.definition);

      // 3. Save localizations if present in the bundle
      if (parsedBundle.template.localizations && draft?.id) {
        for (const loc of parsedBundle.template.localizations) {
          try {
            await saveLocalization(created.id, draft.id, loc.language_code, loc.messages);
          } catch {
            // Non-fatal if localization fails to save immediately
          }
        }
      }

      return created;
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      handleClose();
      await navigate({ to: '/templates/$id/edit', params: { id: created.id } });
    },
  });

  if (!isOpen) return null;

  const processContent = (content: string) => {
    setRawText(content);
    try {
      const json = JSON.parse(content);
      const res = validateTemplateBundle(json);
      if (res.valid && res.bundle) {
        setParsedBundle(res.bundle);
        setTemplateName(res.bundle.template.name);
        setVisibility(res.bundle.template.visibility || 'private');
        setValidationErrors([]);
      } else {
        setParsedBundle(null);
        setValidationErrors(res.errors);
      }
    } catch (e) {
      setParsedBundle(null);
      setValidationErrors([e instanceof Error ? e.message : 'Invalid JSON syntax']);
    }
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) processContent(content);
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const error =
    importMutation.error instanceof Error ? (importMutation.error as ApiError) : undefined;

  const sectionsCount = parsedBundle?.template.definition.sections.length ?? 0;
  const itemsCount =
    parsedBundle?.template.definition.sections.reduce((n, s) => n + s.items.length, 0) ?? 0;
  const rolesCount = parsedBundle?.template.definition.roles.length ?? 0;

  return createPortal(
    <div
      className="tpl-modal__overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="tpl-modal">
        <header className="tpl-modal__head">
          <div>
            <p className="tpl-eyebrow">{t('templates.import_modal_eyebrow')}</p>
            <h2 className="tpl-modal__title">{t('templates.import_modal_title')}</h2>
          </div>
          <button
            type="button"
            className="tpl-modal__close-btn"
            onClick={handleClose}
            aria-label={t('action.close', 'Close')}
          >
            ✕
          </button>
        </header>

        <p className="tpl-modal__lede">{t('templates.import_modal_lede')}</p>

        <div className="tpl-modal__body">
          <div className="tpl-bulk__upload-row">
            <label
              htmlFor={fileInputId}
              className={`tpl-bulk__dropzone ${isDragging ? 'tpl-bulk__dropzone--active' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <span>{t('templates.import_dropzone')}</span>
            </label>
            <input
              id={fileInputId}
              type="file"
              accept=".json,.pius.json"
              className="tpl-bulk__file-input"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>

          <label className="tpl-modal__field">
            <span className="tpl-modal__field-label">{t('templates.import_paste_label')}</span>
            <textarea
              className="tpl-bulk__textarea tpl-modal__mono"
              rows={5}
              value={rawText}
              placeholder="Paste .pius.json or TemplateDefinition JSON content here..."
              onChange={(e) => processContent(e.target.value)}
            />
          </label>

          {validationErrors.length > 0 && (
            <div className="tpl-bulk__error-box">
              <strong>{t('templates.validation_errors_heading')}</strong>
              <ul>
                {validationErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {parsedBundle && (
            <div className="tpl-bulk__summary-box">
              <div className="tpl-bulk__summary-badge">✓ {t('templates.bundle_valid')}</div>

              <label className="tpl-modal__field" style={{ marginTop: '12px' }}>
                <span className="tpl-modal__field-label">{t('templates.name_label')}</span>
                <input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  required
                />
              </label>

              <div className="tpl-bulk__summary-grid">
                <div>
                  <span className="tpl-bulk__stat-label">{t('templates.sections')}</span>
                  <span className="tpl-bulk__stat-val">{sectionsCount}</span>
                </div>
                <div>
                  <span className="tpl-bulk__stat-label">{t('templates.col_template')}</span>
                  <span className="tpl-bulk__stat-val">
                    {itemsCount} {t('templates.items_count', { count: itemsCount })}
                  </span>
                </div>
                <div>
                  <span className="tpl-bulk__stat-label">{t('templates.col_authority')}</span>
                  <span className="tpl-bulk__stat-val">
                    {rolesCount} {rolesCount === 1 ? 'role' : 'roles'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && <Notice referenceId={error.correlationId}>{error.message}</Notice>}
        </div>

        <footer className="tpl-modal__foot">
          <button type="button" className="tpl-btn tpl-btn--ghost" onClick={handleClose}>
            {t('action.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="tpl-btn tpl-btn--primary"
            disabled={!parsedBundle || importMutation.isPending || !templateName.trim()}
            onClick={() => importMutation.mutate()}
          >
            {importMutation.isPending
              ? t('templates.importing')
              : t('templates.btn_import_template')}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
